import throttle from 'lodash/throttle';
import {
  WAVEFORM_PLAYER_DEFAULT_DURATION_SEC,
  WAVEFORM_PLAYER_TIMEUPDATE_FREQ_MILLIS,
  WAVEFORM_SAMPLE_PLAYBACK_RATE,
} from 'utils/constants';
import EventEmitter from 'utils/EventEmitter';
import { millisToSec } from 'utils/time';
import { BaseAudioPlayer, DrawableAudio, PlayerEvent } from './types';

type Handler = () => void;

const SUPPORTED_EVENTS: PlayerEvent[] = ['pause', 'play', 'timeupdate'];

// a "silent" audio player which has no source or associated HTMLMediaElement.
// this player just simulates playing by allowing the caller to sample the waveform
// data, which is a static array of data that is looped when playback is initiated
export default class SampleWaveformPlayer
  implements BaseAudioPlayer, DrawableAudio
{
  private data: number[][];

  private setIntervalId: number | undefined;

  private frameIndex: number;

  private playing: boolean;

  private currentTimeValue: number;

  private emitter: EventEmitter<PlayerEvent>;

  constructor(data: number[][], duration?: number) {
    this.currentTimeValue = 0;
    this.playing = false;
    this.data = data;
    this.frameIndex = Math.floor(Math.random() * this.data.length - 1);
    this.emitter = new EventEmitter();
  }

  private fireTimeupdate = throttle(() => {
    this.emitter.emit('timeupdate');
  }, WAVEFORM_PLAYER_TIMEUPDATE_FREQ_MILLIS);

  public on(event: PlayerEvent, fn: Handler) {
    this.emitter.on(event, fn);
  }

  public off(event: PlayerEvent, fn: Handler) {
    this.emitter.off(event, fn);
  }

  public play() {
    this.playing = true;
    this.emitter.emit('play');
    this.setIntervalId = window.setInterval(() => {
      this.frameIndex = (this.frameIndex + 1) % this.data.length;

      this.currentTimeValue =
        (this.currentTimeValue + millisToSec(WAVEFORM_SAMPLE_PLAYBACK_RATE)) %
        WAVEFORM_PLAYER_DEFAULT_DURATION_SEC;

      this.fireTimeupdate();
    }, WAVEFORM_SAMPLE_PLAYBACK_RATE);
    return Promise.resolve();
  }

  public pause() {
    this.playing = false;
    this.emitter.emit('pause');
    window.clearInterval(this.setIntervalId);
    this.setIntervalId = undefined;
  }

  public supports(event: string) {
    return SUPPORTED_EVENTS.includes(
      event as (typeof SUPPORTED_EVENTS)[number],
    );
  }

  public seek(seconds: number) {
    this.currentTimeValue = seconds;
  }

  public get waveformData() {
    return new Uint8Array(this.data[this.frameIndex]);
  }

  public get isReady() {
    return true;
  }

  public get paused() {
    return !this.playing;
  }

  public ready(): Promise<void> {
    return Promise.resolve();
  }

  public currentTime() {
    return this.currentTimeValue;
  }

  public get waveformDataSize() {
    return this.data[0].length;
  }

  public get duration() {
    return WAVEFORM_PLAYER_DEFAULT_DURATION_SEC;
  }

  public get volume() {
    return 0;
  }

  public set volume(_: number) {
    // not applicable
  }
}
